<script lang="ts">
  import "@lottiefiles/lottie-player";
  import type { Config } from "../declarations/config";
  import type { LottiePlayer } from "@lottiefiles/lottie-player";

  import {
    BackgroundDimensions,
    closedGiftSoundCounter,
    isScrolling,
    selectedActivityIndex,
    todaysGiftOpened,
  } from "../store/AppState";
  import { backgroundDimensions } from "../config/dimensions";
  import { CurrentDay } from "../store/AppState";
  import {
    sendInteractionEvent,
    INTERACTION_TYPE_GIFT,
  } from "../utils/analytics";
  import { afterUpdate } from "svelte";
  import { playClosedGiftSound } from "../utils/AudioManager";

  // Check: https://github.com/LottieFiles/svelte-lottie-player/blob/master/src/components/utils.js
  let player: LottiePlayer;

  export let config: Config;
  export let index: number;
  export let onLoad: () => void;
  export let onClick: () => void;
  export let zIndex: string;

  const xRatio = Number(config.coordinates.x) / backgroundDimensions.width;
  const yRatio = Number(config.coordinates.y) / backgroundDimensions.height;
  const widthRatio = Number(config.width) / backgroundDimensions.width;

  let x = 0;
  let y = 0;
  let width = 0;

  let giftState: number;
  let giftHovered = false;

  const updateGiftState = () => {
    if ($CurrentDay < index + 1) {
      giftState = 2;
    }
    if ($CurrentDay === index + 1) {
      if ($todaysGiftOpened) {
        giftState = 0;
      } else {
        giftState = 1;
      }
    }
    if ($CurrentDay > index + 1) {
      giftState = 0;
    }
    if ($selectedActivityIndex === index) {
      giftState = 3;
    }
  };

  const calculateDimensions = () => {
    x = xRatio * $BackgroundDimensions.width;
    y = yRatio * $BackgroundDimensions.height;
    width = widthRatio * $BackgroundDimensions.width;
    if (index === $CurrentDay - 1) {
      x -= 20;
      y -= 65;
      width += 40;
    } else {
      y *= index % 2 === 1 ? 0.985 : 1;
    }
  };

  window.addEventListener("resize", () => {
    calculateDimensions();
  });

  const handleClick = () => {
    if (window.isClosed && !window.isShowroom) {
      return;
    }
    if (giftState <= 1) {
      onClick();
    }
    if (giftState === 2) {
      player.seek(0);
      player.play();
      playClosedGiftSound($closedGiftSoundCounter);
      $closedGiftSoundCounter = $closedGiftSoundCounter + 1;
      if ($closedGiftSoundCounter > 3) {
        $closedGiftSoundCounter = 0;
      }
    }
    updateGiftState();
    sendInteractionEvent(INTERACTION_TYPE_GIFT, config.order);
  };

  let loopCounter = 0;
  const handleLoop = () => {
    if ($selectedActivityIndex !== null) {
      player.loop = false;
      giftHovered = true;
    }
    if (!giftHovered) {
      loopCounter++;
      if (loopCounter === 3) {
        player.loop = false;
      }
    }
  };
  const handleComplete = () => {
    if (!giftHovered) {
      player.stop();
      player.seek(0);
      loopCounter = 0;

      setTimeout(() => {
        player.play();
        player.loop = true;
      }, 2000);
    }
  };

  const handleLoad = () => {
    player.getLottie().setSubframe(false);
    onLoad();
  };

  afterUpdate(() => {
    if ($isScrolling) {
      player.pause();
    } else if (index === $CurrentDay - 1 && !giftHovered) {
      player.play();
    }
    if ($selectedActivityIndex === $CurrentDay - 1) {
      $todaysGiftOpened = true;
    }
    updateGiftState();
  });
</script>

<div
  class="lottie-container"
  style={`--top: ${y}px; --left: ${x}px; z-index: ${zIndex}`}
>
  <button
    class="gift-hitbox"
    on:pointerup={handleClick}
    on:pointerenter={() => {
      player.play();
      if (giftState !== 2) {
        player.loop = true;
      }
      giftHovered = true;
    }}
    on:pointerleave={() => {
      player.stop();
      player.loop = false;
      player.seek(0);
    }}
  ></button>
  {#key $selectedActivityIndex === index}
    <lottie-player
      on:loop={() => handleLoop()}
      on:complete={() => handleComplete()}
      on:load={() => handleLoad()}
      bind:this={player}
      loop={giftState === 2 ? false : true}
      src={`${__PUBLIC_PATH__}darky/${config.name}/${giftState}/data.json`}
      mode="normal"
      speed="1"
      style={`width: ${width}px;`}
      autoplay={false}
    />
  {/key}
</div>

<style>
  .lottie-container {
    position: absolute;
    top: var(--top);
    left: var(--left);
  }
  .gift-hitbox {
    all: unset;
    height: 50%;
    bottom: 5%;
    width: 100%;
    display: block;
    position: absolute;
    z-index: 4;
    cursor: pointer;
  }
</style>
